import { useTranslation } from 'react-i18next';

import { getMapConfiguration } from '@ngsos/portal-core/agency';

import { NgButtonNavLink } from '@/ui';
import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import { isCoordinateLocation, isMeasuredLocation } from '@/signalr';
import type { GeoPoint, PermanentResidence, PortalLocation } from '@/signalr';
import { changeCallerLocationFromMap } from './actions';
import { createMapLinkModel } from './maps/externalMapLinks';
import { IncidentDetailMapComponent } from './maps/IncidentDetailMapComponent';

export type IncidentDetailMapProps = {
  incidentId: string;
  callerPosition: PortalLocation | null;
  callerPositionHistory: readonly PortalLocation[];
  lastPositionBySource: readonly PortalLocation[];
  permanentResidence?: PermanentResidence | null;
  incidentIsReadOnly: boolean;
};

export function IncidentDetailMap({
  incidentId,
  callerPosition,
  callerPositionHistory,
  lastPositionBySource,
  permanentResidence,
  incidentIsReadOnly,
}: IncidentDetailMapProps) {
  const { t } = useTranslation();
  const agencyConfiguration = useAgencyConfiguration();
  const mapConfiguration = getMapConfiguration(agencyConfiguration);

  const coordinateLocation = callerPosition !== null && isCoordinateLocation(callerPosition) ? callerPosition : null;
  const effectiveMapCenter: GeoPoint =
    coordinateLocation ?? permanentResidence?.coordinates ?? mapConfiguration.defaultPosition;

  const effectiveZoom =
    coordinateLocation !== null
      ? isMeasuredLocation(coordinateLocation) &&
        coordinateLocation.accuracy !== null &&
        coordinateLocation.accuracy > 200
        ? 14
        : 17
      : mapConfiguration.defaultZoom;

  const navigationTargetUrl =
    coordinateLocation !== null
      ? createMapLinkModel(mapConfiguration.navigationMapLink, coordinateLocation, {
          mapyCzAltText: t('Incident_InfoPanel_IconMapyCzAltText'),
          googleMapsAltText: t('Incident_InfoPanel_IconGoogleMapsAltText'),
        }).link
      : null;

  const changeCallerPositionFromMap = async (coordinates: GeoPoint) => {
    await changeCallerLocationFromMap(incidentId, coordinates);
  };

  return (
    <div className="incident-detail-map h-100 position-relative">
      <IncidentDetailMapComponent
        incidentId={incidentId}
        center={effectiveMapCenter}
        zoom={effectiveZoom}
        callerPosition={callerPosition}
        callerPositionHistory={callerPositionHistory}
        lastPositionBySource={lastPositionBySource}
        permanentResidence={permanentResidence}
        onChangeCallerLocation={changeCallerPositionFromMap}
        incidentIsReadOnly={incidentIsReadOnly}
      />

      {navigationTargetUrl !== null && (
        <div className="d-tablet-none position-absolute bottom-0 start-0 end-0 d-flex gap-2 justify-content-center pb-2 z-2">
          <NgButtonNavLink
            icon="fa-light fa-route"
            text={t('Button_Navigate')}
            to={navigationTargetUrl}
            target="_blank"
          />
        </div>
      )}
    </div>
  );
}
